// The F_ADD term of the three instanced batches on paper
// (ui-map-interface.md §2.5 "The glow switch and the passes", R3293).
//
// The dark identity adds a glow as light: an F_ADD instance writes the
// alpha 0, and the one blend `ONE, ONE_MINUS_SRC_ALPHA` then adds its
// colour (`fast/layer.ts` `F_ADD`). Added light disappears on paper. In the
// light identity the same instance is a covered ink: the alpha
// `aColor.a * AP_GLOW_K`, the colour premultiplied by that alpha, the
// same blend, which is then `over`. Every user of the flag (the Beacon
// beam, the effects, the gather looks, the beam pips, the combat looks,
// the spawn light and `RecipeCtx.add` of every look kit) changes with no
// change of its own.
//
// The batch programs carry no shared prelude, so a batch in the light
// identity takes the two constants of `identity.ts` here. In the dark
// identity both names below are '' and the program text stays today's,
// byte for byte (`gfx/addglow.test.ts`).

import { LIGHT } from '../../identity/pick';
import { AP_GLOW_K, IDENTITY_GLSL, IDENTITY_WGSL } from './identity';

/** The identity constants of a GLSL batch program: '' in the dark. */
export const BATCH_IDENTITY_GLSL = LIGHT ? IDENTITY_GLSL : '';

/** The identity constants of a WGSL batch module: '' in the dark. */
export const BATCH_IDENTITY_WGSL = LIGHT ? IDENTITY_WGSL : '';

// The term itself. Each dark text is the line its program carried before
// R3293, spaces and comment included; the light text is the same term
// with the covered alpha. WGSL `select` takes its FALSE arm first.

/** The F_ADD term of the quad batch vertex, GLSL (`gfx/quads.ts`). */
export const QUAD_ADD_GLSL = LIGHT
  ? `  float a = ((flags & 2) != 0) ? aColor.a * AP_GLOW_K : aColor.a;   // F_ADD
  vColor = vec4(aColor.rgb * a, a);`
  : `  float a = ((flags & 2) != 0) ? 0.0 : aColor.a;      // F_ADD
  vColor = vec4(aColor.rgb * aColor.a, a);`;

/** The F_ADD term of the quad batch vertex, WGSL (`wgsl/quads.ts`). */
export const QUAD_ADD_WGSL = LIGHT
  ? `  let a = select(in.aColor.a, in.aColor.a * AP_GLOW_K, (flags & 2) != 0);   // F_ADD
  out.vColor = vec4f(in.aColor.rgb * a, a);`
  : `  let a = select(in.aColor.a, 0.0, (flags & 2) != 0);     // F_ADD
  out.vColor = vec4f(in.aColor.rgb * in.aColor.a, a);`;

/** The F_ADD term of the circle and the segment batch vertex, GLSL: the
 *  hairline fade multiplies the whole colour after it. */
export const STROKE_ADD_GLSL = LIGHT
  ? `  float a = ((flags & 2) != 0) ? aColor.a * AP_GLOW_K : aColor.a;   // F_ADD
  vColor = vec4(aColor.rgb * a, a) * fade;`
  : `  float a = ((flags & 2) != 0) ? 0.0 : aColor.a;         // F_ADD
  vColor = vec4(aColor.rgb * aColor.a, a) * fade;`;

/** The F_ADD term of the circle and the segment batch vertex, WGSL. */
export const STROKE_ADD_WGSL = LIGHT
  ? `  let a = select(in.aColor.a, in.aColor.a * AP_GLOW_K, (flags & 2) != 0);   // F_ADD
  out.vColor = vec4f(in.aColor.rgb * a, a) * fade;`
  : `  let a = select(in.aColor.a, 0.0, (flags & 2) != 0);     // F_ADD
  out.vColor = vec4f(in.aColor.rgb * in.aColor.a, a) * fade;`;

/**
 * The alpha a batch writes for one instance: the TypeScript statement of
 * the term above, so a test reads what a recorded instance draws. In the
 * dark identity an F_ADD instance writes 0 (added light); on paper it
 * writes `alpha * AP_GLOW_K` (a covered ink).
 */
export function batchAlpha(flags: number, alpha: number): number {
  if ((flags & 2) === 0) return alpha;
  return LIGHT ? alpha * AP_GLOW_K : 0;
}
